/**
 * Google Drive glyphs used by the connector surfaces (composer menu, picker,
 * attachment chips, artifact panel). The Drive logo keeps Google's colours on
 * purpose — it identifies a third-party service, exactly like the Drive logo
 * inside Claude.ai's "+" menu — every other icon here follows currentColor.
 */

import type { DriveKind } from '../../services/driveService';

export function GoogleDriveGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 87.3 78" width={size} height={size * (78 / 87.3)} aria-hidden="true" focusable="false">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}

/** Monochrome file-kind glyph for picker rows. Google-native types are drawn
 *  with the app's signature colour so a Doc, Sheet and Slides read at a glance. */
export function DriveKindIcon({ kind, size = 18 }: { kind: DriveKind; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true } as const;
  switch (kind) {
    case 'folder':
      return (
        <svg {...common} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
      );
    case 'doc':
    case 'word':
    case 'pages':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" fill={kind === 'doc' ? '#4285f4' : kind === 'pages' ? '#F28C1E' : '#2b5797'} />
          <path d="M8 9h8M8 12.5h8M8 16h5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'sheet':
    case 'excel':
    case 'numbers':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" fill={kind === 'sheet' ? '#34a853' : kind === 'numbers' ? '#1DA34B' : '#1d6f42'} />
          <path d="M8 9h8v7H8zM12 9v7M8 12.5h8" stroke="#fff" strokeWidth="1.4" />
        </svg>
      );
    case 'slides':
    case 'powerpoint':
    case 'keynote':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" fill={kind === 'slides' ? '#fbbc04' : kind === 'keynote' ? '#1E88E5' : '#d24726'} />
          <rect x="7.5" y="8" width="9" height="7" rx="1" stroke="#fff" strokeWidth="1.5" />
        </svg>
      );
    case 'pdf':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" fill="#ea4335" />
          <text x="12" y="15.5" textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">PDF</text>
        </svg>
      );
    case 'image':
      return (
        <svg {...common} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="m20 16-5-5-8 8" />
        </svg>
      );
    default:
      return (
        <svg {...common} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
          <path d="M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
          <path d="M14 2v6h6" />
        </svg>
      );
  }
}

export function ExternalLinkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  );
}
